import { useEffect, useState } from 'react';
import { ConnectedRouter } from 'connected-react-router';
import { Provider } from 'react-redux';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { addLocaleData, IntlProvider } from 'react-intl';
import { PersistGate } from 'redux-persist/integration/react';
import de from 'react-intl/locale-data/de';
import { ReactNotifications } from 'react-notifications-component';
import { CONFIG, environment, VERSION } from 'config';
import { Footer, Navigation } from 'Component';
import { ThemeProvider } from 'Container';
import { browserHistory, Persistor, store } from 'redux/configureStore';
import { translations } from './locales/translations';
import { defaultLocale } from './locales/default';
import { pushDataLayerPageView } from 'components/GtmHelper';
import { AuthenticationContainer } from 'containers/Authentication/AuthenticationContainer';
import { ScrollToTop } from 'components/ScrollToTop/ScrollToTop';
import { PageTitleContainer } from './containers/PageTitleContainer/PageTitleContainer';
import { Router } from 'components/Application/Router/Router';
import { RouteDataProvider } from 'Application/RouteDataProvider/RouteDataProvider';
import '@missionme/mime_elements/dist/public/Css/Global.css';
import '@missionme/mime_elements/dist/public/Css/GlobalColors.css';
import '@missionme/mime_elements/dist/public/Css/BootstrapUtils.css';
import ScrollToHash from './components/Container/ScrollToHash/ScrollToHash';

addLocaleData([...de]);

export const App = () => {
  const [currentRoute, setCurrentRoute] = useState(null);

  useEffect(() => {
    pushDataLayerPageView(window.location.pathname);

    Bugsnag.start({
      apiKey: CONFIG.BUGSNAG_API_KEY,
      plugins: [new BugsnagPluginReact()],
      releaseStage: environment,
      appVersion: VERSION,
      enabledErrorTypes: {
        unhandledExceptions: true,
        unhandledRejections: false
      }
    });
  }, []);

  const locale = defaultLocale;

  const messages = translations[locale];

  return (
    <IntlProvider locale={locale} key={locale} messages={messages}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={Persistor}>
          <ConnectedRouter history={browserHistory}>
            <RouteDataProvider value={{ currentRoute, setCurrentRoute }}>
              <ScrollToTop />
              <AuthenticationContainer />
              <PageTitleContainer />
              <ThemeProvider>
                <div aria-live="polite" aria-atomic="true" className="sr-only" id="route-announcer" />
                <ReactNotifications />
                <Navigation />
                <Router />
                <ScrollToHash />
                <Footer />
              </ThemeProvider>
            </RouteDataProvider>
          </ConnectedRouter>
        </PersistGate>
      </Provider>
    </IntlProvider>
  );
};

// allow cypress to watch the store
if (window.Cypress) {
  window.store = store;
}
